"use client"

import type { ConsentStoreState } from "c15t"
import { has } from "c15t"
import type { AllConsentNames } from "@c15t/nextjs"
import { useContext, useMemo, type Context } from "react"
import { ConsentStateContext as ConsentStateContextRaw } from "./c15t-consent-state-context"

/** Narrow shape needed for {@link has}; matches c15t provider context value. */
type ConsentManagerReactValue = {
  state: ConsentStoreState
  store: unknown
  manager: unknown
}

const ConsentStateContext = ConsentStateContextRaw as Context<ConsentManagerReactValue | undefined>

export { useConsentManager } from "@c15t/nextjs"

/**
 * Read-only hook: returns whether a given consent category is currently granted.
 * Re-renders when consent changes.
 *
 * Uses `ConsentStateContext` + `has()` from `c15t` instead of `useConsentManager()`
 * so callers that are briefly outside `ConsentManagerProvider` (e.g. some dev / HMR
 * trees) do not crash the app — non-`necessary` categories read as not granted.
 */
export function useHasConsent(category: AllConsentNames): boolean {
  const ctx = useContext(ConsentStateContext)
  return useMemo(() => {
    if (ctx == null) {
      return category === "necessary"
    }
    const { consents, policyCategories, policyScopeMode } = ctx.state
    return has(category, consents, { policyCategories, policyScopeMode })
  }, [category, ctx])
}
